import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Trash2, Building2, CreditCard, Landmark, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AccountsPage({ companyId }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("bank");
  const [formInstitution, setFormInstitution] = useState("");
  const [formLast4, setFormLast4] = useState("");
  const [formBalance, setFormBalance] = useState("0");
  const [saving, setSaving] = useState(false);
  const [reconcileData, setReconcileData] = useState({});
  const [reconcilingId, setReconcilingId] = useState(null);

  const loadAccounts = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.get(`/companies/${companyId}/accounts`);
      setAccounts(res.data);
      // Fetch reconciliation data for each account
      const reconData = {};
      await Promise.all(res.data.map(async (acct) => {
        try {
          const r = await api.get(`/accounts/${acct.account_id}/reconcile`);
          reconData[acct.account_id] = r.data;
        } catch {}
      }));
      setReconcileData(reconData);
    } catch (err) {
      console.error("Load accounts error:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/accounts`, {
        name: formName,
        account_type: formType,
        institution: formInstitution,
        account_number_last4: formLast4,
        balance: parseFloat(formBalance) || 0,
      });
      toast.success("Account created");
      setShowAdd(false);
      resetForm();
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormName("");
    setFormType("bank");
    setFormInstitution("");
    setFormLast4("");
    setFormBalance("0");
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm("Delete this account and all its transactions?")) return;
    try {
      await api.delete(`/accounts/${accountId}`);
      toast.success("Account deleted");
      loadAccounts();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleReconcile = async (accountId) => {
    setReconcilingId(accountId);
    try {
      const res = await api.post(`/accounts/${accountId}/reconcile`);
      toast.success(`Reconciled: balance set to $${res.data.calculated_balance.toLocaleString()}`);
      loadAccounts();
    } catch (err) {
      toast.error("Reconciliation failed");
    } finally {
      setReconcilingId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="accounts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Accounts</h1>
          <p className="text-muted-foreground mt-1">Manage bank accounts and credit cards</p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="add-account-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Account
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 bg-secondary" />)}
        </div>
      ) : accounts.length === 0 ? (
        <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
          <CardContent className="p-12 text-center" data-testid="no-accounts">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-white font-medium">No accounts yet</p>
            <p className="text-muted-foreground text-sm mt-1">Add a bank account or credit card to start tracking transactions.</p>
            <Button onClick={() => setShowAdd(true)} className="mt-4" data-testid="add-first-account-btn">
              <Plus className="w-4 h-4 mr-2" /> Add First Account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map(acct => (
            <Card key={acct.account_id} className="border-border/50 hover:border-primary/30 transition-colors"
              style={{ background: 'hsl(233 30% 8%)' }} data-testid={`account-card-${acct.account_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ background: acct.account_type === 'bank' ? 'hsl(225 100% 60% / 0.1)' : 'hsl(160 100% 44% / 0.1)' }}>
                      {acct.account_type === 'bank' ? (
                        <Landmark className="w-5 h-5 text-primary" />
                      ) : (
                        <CreditCard className="w-5 h-5 text-profit" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{acct.name}</p>
                      <p className="text-xs text-muted-foreground">{acct.institution}</p>
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDelete(acct.account_id)} data-testid={`delete-account-${acct.account_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>

                {/* Balance Section */}
                <div className="mb-4">
                  <div className="flex items-end justify-between">
                    <div>
                      <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                        {acct.account_type === 'bank' ? 'Bank Account' : 'Credit Card'}
                      </Badge>
                      {acct.account_number_last4 && (
                        <p className="text-xs text-muted-foreground mt-2 font-mono">****{acct.account_number_last4}</p>
                      )}
                    </div>
                    <p className="text-xl font-bold font-mono text-white">${acct.balance?.toLocaleString() || "0"}</p>
                  </div>
                </div>

                {/* Reconciliation Section */}
                {reconcileData[acct.account_id] && (() => {
                  const rd = reconcileData[acct.account_id];
                  return (
                    <div className="pt-3 border-t border-border/30 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Calculated from {rd.transaction_count} txns</span>
                        <span className="font-mono text-white">${rd.calculated_balance.toLocaleString()}</span>
                      </div>
                      {rd.is_reconciled ? (
                        <div className="flex items-center gap-1.5 text-xs text-profit">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Reconciled
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1.5 text-xs text-warning cursor-help">
                                  <AlertTriangle className="w-3.5 h-3.5" />
                                  Difference: <span className="font-mono">${rd.difference.toLocaleString()}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Stated balance differs from calculated balance</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <Button size="sm" variant="outline" className="h-7 text-xs border-border text-white"
                            onClick={() => handleReconcile(acct.account_id)}
                            disabled={reconcilingId === acct.account_id}
                            data-testid={`reconcile-${acct.account_id}`}>
                            <RefreshCw className={`w-3 h-3 mr-1.5 ${reconcilingId === acct.account_id ? 'animate-spin' : ''}`} />
                            Reconcile
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Account Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-card border-border" data-testid="add-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Add Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 py-2">
            <div>
              <Label className="text-muted-foreground text-sm">Account Name *</Label>
              <Input value={formName} onChange={e => setFormName(e.target.value)}
                placeholder="e.g., Chase Business Checking" required
                className="mt-1.5 bg-secondary border-border text-white" data-testid="account-name-input" />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Account Type</Label>
              <Select value={formType} onValueChange={setFormType}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="account-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="bank" className="text-white">Bank Account</SelectItem>
                  <SelectItem value="credit_card" className="text-white">Credit Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Institution</Label>
              <Input value={formInstitution} onChange={e => setFormInstitution(e.target.value)}
                placeholder="e.g., Chase, Bank of America"
                className="mt-1.5 bg-secondary border-border text-white" data-testid="account-institution-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-sm">Last 4 Digits</Label>
                <Input value={formLast4} onChange={e => setFormLast4(e.target.value)}
                  placeholder="1234" maxLength={4}
                  className="mt-1.5 bg-secondary border-border text-white font-mono" data-testid="account-last4-input" />
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Balance</Label>
                <Input type="number" step="0.01" value={formBalance} onChange={e => setFormBalance(e.target.value)}
                  className="mt-1.5 bg-secondary border-border text-white font-mono" data-testid="account-balance-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setShowAdd(false)} className="border-border text-white">Cancel</Button>
              <Button type="submit" disabled={saving || !formName.trim()} data-testid="account-submit-btn">
                {saving ? "Creating..." : "Create Account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
